import { MeiliSearch, type MultiSearchResult, type RecordAny } from "meilisearch"
import { clickOutside, dateLocaleString } from "../../../auth/helpers"
import { type ContentOptions } from "../../global-search.options"

import { type Hit } from "../../models/hit"

import styles from "./global-search.scss?inline"
import templateHTML from './global-search.component.html?raw'
import { i18n } from "../../../internationalize/i18n.service"
import { getIndexes, getOptions, getContentTypeForI18n } from "../../global-search.options"

import { calendar } from "../../../assets/icons"

export class GlobalSearchComponent extends HTMLElement {
  private _client?: MeiliSearch

  private _searchInput?: HTMLInputElement
  private _hitList?: HTMLOListElement
  private _closeButton?: HTMLButtonElement

  private expandedCategories = new Set<string>()
  private lastResults: any[] = []

  private searchLimit = 10;
  private searchDefaultLimit = 3;

  constructor() {
    super()
    this.attachShadow({ mode: "open" })
    const templateContainer = document.createElement('div');
    templateContainer.innerHTML = templateHTML;
    const template = templateContainer.querySelector('template');

    // Create and append style elements
    const style = document.createElement('style');
    style.textContent = styles;
    this.shadowRoot?.appendChild(style);
    this.shadowRoot?.appendChild(template!.content.cloneNode(true))
  }

  get dialog() {
    return this.shadowRoot?.querySelector<HTMLDialogElement>('dialog')
  }

  inputHandler(e: InputEvent) {
    const value = (e.target as HTMLInputElement).value;
    if (value) {
      this.search(value, this.searchLimit);
    }
    else {
      this.search('');
    }
  }

  clickHandler(e: MouseEvent) {
    if (clickOutside(e)) { this.close() }
  }

  async connectedCallback() {
    this._searchInput = this.shadowRoot?.querySelector<HTMLInputElement>("input[type=search]")!
    this._hitList = this.shadowRoot?.querySelector<HTMLOListElement>(".hit-list")!
    this._closeButton = this.shadowRoot?.querySelector<HTMLButtonElement>(".close-btn")!

    const host = import.meta.env.VITE_MEILI_ENDPOINT
    const apiKey = import.meta.env.VITE_MEILI_CLIENT_KEY

    if (!host || !apiKey) {
      this.showSearchError('Search is not configured')
      return
    }

    try {
      this._client = new MeiliSearch({ host, apiKey })
    } catch {
      this.showSearchError('Search failed to initialize')
      return
    }

    this._searchInput.placeholder = i18n.t("searchPlaceholder")

    this._searchInput.addEventListener("input", this.inputHandler.bind(this) as EventListener)
    this._closeButton.addEventListener("click", this.close.bind(this))
    this.shadowRoot?.addEventListener("click", this.clickHandler.bind(this) as EventListener)
    this.shadowRoot?.querySelectorAll<HTMLDivElement>(".no-result-container *[i18n]")?.forEach((elem: HTMLElement) => elem.innerText = i18n.t(elem.attributes.getNamedItem("i18n")?.value ?? ''))
    this.search('')

    this.shadowRoot?.addEventListener('click', this.showMoreEventHandler.bind(this) as EventListener)
  }

  setupAnalyticsListeners() {
    this.shadowRoot?.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', () => {
        window.tbwServices.analyticsService.track("search_bar_click")
      })
    })
  }

  showMoreEventHandler(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (target.classList.contains('show-more-btn')) {
      const category = target.getAttribute('data-category');
      if (category) {
        if (this.expandedCategories.has(category)) {
          this.expandedCategories.delete(category);
        } else {
          this.expandedCategories.add(category);
        }
        this._hitList!.innerHTML = this.lastResults
          .filter((value: any) => value.hits && value.hits.length > 0)
          .map(this.histListToView.bind(this)).join('');
        this.setupAnalyticsListeners()
      }
    }
  }

  disconnectedCallback() {
    this._searchInput?.removeEventListener("input", this.inputHandler.bind(this) as EventListener)
    this._closeButton?.addEventListener("click", this.close.bind(this))
    this.removeEventListener("click", this.clickHandler)
  }

  show() {
    this.dialog?.showModal()
    document.body.style.overflow = 'hidden'
  }

  close() {
    this.dialog?.close()
    document.body.style.overflow = ''
  }

  hitListOptions = (hitList: MultiSearchResult<RecordAny>) => getOptions(hitList.indexUid)

  histListToView(hitList: MultiSearchResult<RecordAny>): string {
    const options = this.hitListOptions(hitList)
    const icon = options?.icon || ''
    const heading = `\n<p>${i18n.t('content.' + getContentTypeForI18n(hitList.indexUid))}</p>\n`
    const limit = this.searchDefaultLimit;

    const isExpanded = this.expandedCategories.has(hitList.indexUid);
    const hitsToShow = isExpanded ? hitList.hits : hitList.hits.slice(0, limit);
    const hitViews = hitsToShow.map((h) => this.hitToView(options, h as Hit)).join('');
    const showToggleButton = hitList.hits.length > limit
      ? `<button class="show-more-btn" data-category="${hitList.indexUid}">${isExpanded ? (i18n.t('showLess')) : (i18n.t('showMore'))}</button>`
      : '';

    return `
      <div class="hit-list-container">
        <div class="hit-list-header">
        <div class="hit-list-title">
          <div class="hit-item-icon hit-item-icon--16">${icon}</div> ${heading}
        </div>
          ${showToggleButton}
        </div>
        <div class="hit-list-hits">
          ${hitViews}
        </div>
      </div>
    `
  }

  hitToView(options: ContentOptions, hit: Hit) {
    const dateHTML = (date: number) =>
      `
      <div class="hit-item-date">
        <div class="hit-item-icon hit-item-icon--16">${calendar}</div>
        <div class="hit-item-date-text">${dateLocaleString(date)}</div>
      </div>
    `
    const date = hit.createdOn

    const thumbnail = hit.thumbnail
    const icon = options.icon
    const isIcon = options.isIcon

    return `
      <li class="hit-item">
        <div class="hit-item-content">
          <div class="hit-item-thumbnail">
            ${isIcon ?
        thumbnail ? `<div class="hit-item-icon hit-item-icon--row"><img src="${thumbnail}"></div>`
          : `<div class="hit-item-icon hit-item-icon--row">${icon}</div>`
        : `<img src="${thumbnail}">`}
          </div>
          <a class="hit-item-link" href=/${hit.url}>${hit.name}</a>
        </div>
        ${options.hasDate ? dateHTML(date!) : ""}
        <a class="hit-item-see caption1" href=/${hit.url}>${i18n.t("see")}</a>
      </li>`
  }

  showSearchError(message: string) {
    const hitList = this.shadowRoot?.querySelector('.hit-list')
    if (hitList) {
      hitList.innerHTML = `<p class="search-error">${message}</p>`
    }
  }

  async search(query: string, overrideLimit?: number): Promise<void> {
    const indexes = getIndexes();

    if (query) {
      this.expandedCategories.clear();
    }

    const queries = indexes.map(i => {
      const opts = getOptions(i)
      const queryOpts: {
        indexUid: string
        q: string
        limit: number
        sort?: string[]
      } = {
        indexUid: i,
        q: query,
        limit: overrideLimit ?? opts.limit,
      }
      if (opts.sort != null && opts.sort !== '') {
        queryOpts.sort = Array.isArray(opts.sort) ? opts.sort : [opts.sort]
      }
      return queryOpts
    })

    // @ts-ignore: MeiliSearch types may not match perfectly, but this is the correct structure
    const hitLists = await this._client.multiSearch({ queries });
    this.lastResults = hitLists.results;
    this._hitList!.innerHTML = hitLists.results
      .filter((value: any) => value.hits && value.hits.length > 0)
      .map(this.histListToView.bind(this)).join('');
    this.setupAnalyticsListeners()
  }
}

